import type { ComercialUnitRow } from "@/services/comercial";
import type { ChaoUnitMetrics } from "@/services/chaoDeObra";
import { ROUTES } from "@/config/constants";
export function chaoUnitRoute(id: string): string {
  return `${ROUTES.CHAO_DE_OBRA}/unidade/${encodeURIComponent(id)}`;
}

export function listUnitBadge(name: string, unitId: string): string {
  const tail = (name.split("—").pop() ?? name.split("-").pop() ?? name).trim();
  return tail.match(/(\d+)/)?.[1] ?? (unitId.length > 2 ? unitId.slice(-2) : unitId);
}

/** Compara IDs de centro de custo (49 === "49"). */
export function costCenterIdsMatch(a: string, b: string): boolean {
  const x = String(a ?? "").trim();
  const y = String(b ?? "").trim();
  if (!x || !y) return false;
  if (x === y) return true;
  if (/^\d+$/.test(x) && /^\d+$/.test(y)) return Number(x) === Number(y);
  return false;
}

export function unitBelongsToCostCenter(unit: ComercialUnitRow, costCenterId: string): boolean {
  const want = String(costCenterId).trim();
  if (!want) return false;
  return (
    costCenterIdsMatch(unit.costCenterId, want) ||
    costCenterIdsMatch(unit.enterpriseId, want)
  );
}

export function filterUnitsByCostCenter(
  list: ComercialUnitRow[],
  costCenterIds: string[],
): ComercialUnitRow[] {
  const ids = costCenterIds.map((x) => String(x).trim()).filter(Boolean);
  if (!ids.length) return [];
  return list.filter((u) => ids.some((cc) => unitBelongsToCostCenter(u, cc)));
}

/** Contagem oficial por CC (Gerencial / units-by-cc) com fallback na lista carregada. */
export function resolveUnitCountForCostCenters(
  costCenterIds: string[],
  registryCountByCc: Record<string, number>,
  unitsByCC: Array<{ costCenterId: string; unitCount: number }>,
): number {
  let total = 0;
  for (const cc of costCenterIds) {
    const official = unitsByCC.find((u) => costCenterIdsMatch(u.costCenterId, cc))?.unitCount;
    const registry = registryCountByCc[cc] ?? 0;
    total += official != null && official > 0 ? official : registry;
  }
  return total;
}

export function buildRegistryCountByCc(
  units: ComercialUnitRow[],
  costCenterIds: string[],
): Record<string, number> {
  const m: Record<string, number> = {};
  for (const cc of costCenterIds) {
    m[cc] = units.filter((u) => unitBelongsToCostCenter(u, cc)).length;
  }
  return m;
}

export function normalizeUnitSearch(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

/** Busca por nome da casa, ID, quadra/UC e rótulo do empreendimento. */
/** Rótulo da UC: vínculo manual (por CC) → API Sienge → vazio. */
export function resolveConstructiveUnitLabel(
  unit: ComercialUnitRow,
  costCenterId: string,
  linksByCc: Record<string, Record<string, string>>,
): string {
  const cc = String(costCenterId).trim();
  const uid = String(unit.id).trim();
  const linked = linksByCc[cc]?.[uid];
  if (linked?.trim()) return linked.trim();
  return (unit.constructiveUnitName ?? "").trim();
}

/** Filtra por uma ou mais UCs (rótulos do orçamento), usando vínculos quando existirem. */
export function filterUnitsByConstructiveUnits(
  units: ComercialUnitRow[],
  costCenterIds: string[],
  selectedLabels: string[],
  linksByCc: Record<string, Record<string, string>>,
): ComercialUnitRow[] {
  const selected = new Set(selectedLabels.map((x) => String(x).trim()).filter(Boolean));
  if (selected.size === 0) return units;
  const ccList = costCenterIds.map((x) => String(x).trim()).filter(Boolean);
  return units.filter((u) => {
    const cc = ccList.find((id) => String(u.costCenterId).trim() === id) ?? ccList[0] ?? "";
    const lab = resolveConstructiveUnitLabel(u, cc, linksByCc);
    return lab.length > 0 && selected.has(lab);
  });
}

export function filterUnitsBySearch(units: ComercialUnitRow[], query: string): ComercialUnitRow[] {
  const q = normalizeUnitSearch(query);
  if (!q) return units;
  return units.filter((u) => {
    const hay = normalizeUnitSearch(
      `${u.name} ${u.id} ${u.scopeLabel} ${u.constructiveUnitName} ${u.constructiveUnitId}`,
    );
    return hay.includes(q);
  });
}

export function sortUnitsByLastUpdate(
  units: ComercialUnitRow[],
  metrics: Record<string, ChaoUnitMetrics>,
): ComercialUnitRow[] {
  return [...units].sort((a, b) => {
    const pa = metrics[a.id]?.lastUpdate ?? "";
    const pb = metrics[b.id]?.lastUpdate ?? "";
    return pb.localeCompare(pa);
  });
}

export interface ChaoAvancoFisicoPorUcRow {
  label: string;
  avgPct: number;
  unitCount: number;
}

/** Média simples do avanço físico (`generalPct`) agrupada por unidade construtiva. */
export function computeAvancoFisicoPorUc(
  units: ComercialUnitRow[],
  metrics: Record<string, ChaoUnitMetrics>,
  costCenterIds: string[],
  linksByCc: Record<string, Record<string, string>>,
): ChaoAvancoFisicoPorUcRow[] {
  const ccList = costCenterIds.map((x) => String(x).trim()).filter(Boolean);
  const groups = new Map<string, number[]>();

  for (const unit of units) {
    const cc =
      ccList.find((id) => costCenterIdsMatch(unit.costCenterId, id)) ??
      ccList[0] ??
      String(unit.costCenterId).trim();
    const label = resolveConstructiveUnitLabel(unit, cc, linksByCc);
    if (!label) continue;
    const pct = metrics[unit.id]?.generalPct ?? 0;
    const bucket = groups.get(label) ?? [];
    bucket.push(pct);
    groups.set(label, bucket);
  }

  return [...groups.entries()]
    .map(([label, pcts]) => ({
      label,
      avgPct: Math.round((pcts.reduce((s, x) => s + x, 0) / pcts.length) * 10) / 10,
      unitCount: pcts.length,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

export function countUnitsSemUc(
  units: ComercialUnitRow[],
  costCenterIds: string[],
  linksByCc: Record<string, Record<string, string>>,
): number {
  const ccList = costCenterIds.map((x) => String(x).trim()).filter(Boolean);
  return units.filter((unit) => {
    const cc =
      ccList.find((id) => costCenterIdsMatch(unit.costCenterId, id)) ??
      ccList[0] ??
      String(unit.costCenterId).trim();
    return !resolveConstructiveUnitLabel(unit, cc, linksByCc);
  }).length;
}
